import type { ChargeClass } from '@aralere/shared-types';
import { brandLocalDate } from './brand-date.js';

// ──────────────────────────────────────────────────────────────────────────────
// Charge-class status as the SPA sees it (04 #60 WS2b). MIRRORS the server's one predicate in
// api/src/lib/charge-classes.ts (chargeClassStatus / isChargeClassActive), the WS2a preview-mirror
// precedent: the server stays the authority on every save, issue and bulk action; this exists so
// Settings and the draft editor render a scheduled or passed deactivation the same way the server
// resolves it. Dates are BRAND-LOCAL (businesses.timezone, absent = America/Los_Angeles), never
// UTC, and are compared as YYYY-MM-DD strings.
// ──────────────────────────────────────────────────────────────────────────────

export type ChargeClassStatus = 'active' | 'scheduled' | 'inactive';

// brandLocalDate lives in ./brand-date (the ONE SPA brand-clock helper, 04 #136); the barrel
// exports it from there, so every importer of '@aralere/shared-ui' is unchanged.

/** 'active' = usable today with no deactivation date; 'scheduled' = active today, deactivates on a
 *  future date; 'inactive' = active:false, or the date is today or earlier in the brand timezone. */
export function chargeClassStatus(
  cls: Pick<ChargeClass, 'active' | 'deactivatesAt'>,
  timezone?: string | null,
  now: Date = new Date(),
): ChargeClassStatus {
  if (cls.active !== true) return 'inactive';
  const at = cls.deactivatesAt ?? null;
  if (!at) return 'active';
  return at <= brandLocalDate(timezone, now) ? 'inactive' : 'scheduled';
}

/** Whether the class may be applied to a NEW invoice today (a scheduled class still can). */
export function isChargeClassActiveNow(
  cls: Pick<ChargeClass, 'active' | 'deactivatesAt'>,
  timezone?: string | null,
  now: Date = new Date(),
): boolean {
  return chargeClassStatus(cls, timezone, now) !== 'inactive';
}

/** "Sep 18, 2026" for a YYYY-MM-DD date, without timezone shifting (the date is already local). */
export function formatIsoDay(iso: string): string {
  const [y, m, d] = iso.split('-').map((p) => parseInt(p, 10));
  if (!y || !m || !d) return iso;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { timeZone: 'UTC', year: 'numeric', month: 'short', day: 'numeric' });
}
